import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { CriteriaPaneProps } from './types';
import {
  availableSections,
  isSectionConfigured,
  type CriteriaSectionDef,
  type CriteriaTabKey,
  type SectionContext,
} from './sections';
import { SectionStatusProvider } from './FormComponents';
import { useSectionContext } from './sections/context';

interface SectionSlotProps {
  id: string;
  highlighted: boolean;
  /** Marks the section as configured. Only the tabs set it; on the Summary everything is. */
  configured?: boolean;
  children: React.ReactNode;
}

/**
 * Where a section sits in a tab or the Summary. It is the target the criteria search scrolls
 * to, and briefly rings the section it just jumped to so the eye lands on it.
 */
export function SectionSlot({ id, highlighted, configured = false, children }: SectionSlotProps) {
  return (
    <Box
      data-criteria-section={id}
      sx={{
        // Clears the panel's sticky header when scrolled into view.
        scrollMarginTop: 96,
        borderRadius: 1,
        transition: 'box-shadow 300ms ease',
        boxShadow: (theme) => (highlighted ? `0 0 0 2px ${theme.palette.primary.main}` : 'none'),
      }}
    >
      <SectionStatusProvider value={{ configured }}>{children}</SectionStatusProvider>
    </Box>
  );
}

interface SectionBodyProps {
  section: CriteriaSectionDef;
  paneProps: CriteriaPaneProps;
}

const sameErrors = (a: unknown, b: unknown) => a === b || JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});

/**
 * A section's own component, re-rendered only when something it shows changed: one of its
 * stateKeys, the validation errors, or the source type. Without this, every keystroke rebuilt
 * all ~25 sections and their ~170 inputs — the whole reason editing on a big offer lagged.
 */
export const SectionBody = React.memo(
  function SectionBody({ section, paneProps }: SectionBodyProps) {
    const { Component } = section;
    return <Component {...paneProps} />;
  },
  (prev, next) =>
    prev.section === next.section &&
    prev.paneProps.sourceType === next.paneProps.sourceType &&
    prev.paneProps.setState === next.paneProps.setState &&
    prev.paneProps.markFieldTouched === next.paneProps.markFieldTouched &&
    next.section.stateKeys.every((key) => prev.paneProps.state[key] === next.paneProps.state[key]) &&
    sameErrors(prev.paneProps.errors, next.paneProps.errors)
);

interface CriteriaTabPaneProps extends CriteriaPaneProps {
  tab: CriteriaTabKey;
  context: SectionContext;
  highlightId?: string | null;
}

/** One editing tab: every section registered for it, stacked. */
export function CriteriaTabPane({ tab, context, highlightId = null, ...paneProps }: CriteriaTabPaneProps) {
  const sections = availableSections(context).filter((section) => section.tab === tab);
  return (
    <Stack spacing={2}>
      {sections.map((section) => (
        <SectionSlot
          key={section.id}
          id={section.id}
          highlighted={highlightId === section.id}
          configured={isSectionConfigured(section, paneProps.state, context)}
        >
          <SectionBody section={section} paneProps={paneProps} />
        </SectionSlot>
      ))}
    </Stack>
  );
}

/*
 * The per-tab panes under their old names, for anything that renders one on its own. The
 * panel itself goes through CriteriaTabPane with a shared context.
 */
function paneFor(tab: CriteriaTabKey) {
  function Pane(props: CriteriaPaneProps) {
    const context = useSectionContext(props.sourceType);
    return <CriteriaTabPane tab={tab} context={context} {...props} />;
  }
  return Pane;
}

export const UsersPane = paneFor('users');
export const AttributePane = paneFor('attribute');
export const AffiliateQualityScorePane = paneFor('affiliateQualityScore');
export const PrePingPane = paneFor('preping');
export const DailyRestrictionPane = paneFor('daily');
export const AnalysisPane = paneFor('analysis');
